import type { TemplateContext } from "@unbox/api";
import { Eye } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useTemplateContext } from "@/api/assurance/actions";
import { cx } from "@/utils/cx";
import { formatCreatorIdShort } from "@/utils/formatters";
import { ModalCustomMessageExample } from "./modal-custom-message-example";
import type { ActionStep } from "./VideoDetail";

type WhatsNextTimelineProps = {
  steps: ActionStep[];
  videoId: string;
  creatorId?: string;
  dismissedFlagIds: string[];
  stepsEditedBeforeDismissal: number[];
  messageOverrides: Record<number, string>;
  onMessageOverrideChange: (step: number, content: string | null) => void;
  onViewPolicy?: () => void;
};

const EditedBeforeDismissalNotice = ({ step, onReset }: { step: number; onReset: (step: number) => void }) => (
  <p role="status" className="text-xs leading-[18px] text-utility-warning-700">
    You dismissed a risk after editing this message. Review the risks mentioned in the message, or{" "}
    <button type="button" onClick={() => onReset(step)} className="underline decoration-dotted">
      reset it to the policy message
    </button>
    .
  </p>
);

export const WhatsNextTimeline = ({
  steps,
  videoId,
  creatorId,
  dismissedFlagIds,
  stepsEditedBeforeDismissal,
  messageOverrides,
  onMessageOverrideChange,
  onViewPolicy,
}: WhatsNextTimelineProps) => {
  const [previewStep, setPreviewStep] = useState<ActionStep | undefined>(undefined);
  // Synthetic entries (the "Wait Nd" and branch-arm labels) carry no step number, so they
  // can neither hold an override nor be edited.
  const previewStepNumber = previewStep?.step;
  const previewOverride = previewStepNumber === undefined ? undefined : messageOverrides[previewStepNumber];

  // Lazy: only fetch the template context once the user opens the preview modal.
  const { data: templateContext } = useTemplateContext(
    previewStep ? { mode: "include", videoIds: [videoId] } : null,
    dismissedFlagIds,
  );
  const creatorData = templateContext?.creators.find((c) => c.creatorId === creatorId);

  const previewContext: TemplateContext | undefined = useMemo(() => {
    if (!creatorId || !creatorData || !templateContext) return undefined;
    const fallbackRiskExplanations =
      creatorData.flagExplanations.length > 0 ? [creatorData.flagExplanations.join("\n")] : undefined;
    return {
      creatorName: formatCreatorIdShort(creatorId),
      shopName: templateContext.shopName,
      videoUrls: creatorData.videoUrls.length > 0 ? creatorData.videoUrls : undefined,
      flagTypes: creatorData.flagTypes.length > 0 ? creatorData.flagTypes : undefined,
      flagExplanations: creatorData.flagExplanations.length > 0 ? creatorData.flagExplanations : undefined,
      productNameList: creatorData.productNameList.length > 0 ? creatorData.productNameList : undefined,
      creatorRiskExplanations:
        creatorData.creatorRiskExplanations.length > 0 ? creatorData.creatorRiskExplanations : fallbackRiskExplanations,
    };
  }, [creatorId, creatorData, templateContext]);

  if (steps.length === 0) return null;

  return (
    <>
      <div className="flex flex-col w-full">
        {steps.map((step, i) => (
          <div key={i} className="flex gap-2">
            <div className="w-3.5 shrink-0 flex flex-col items-center pt-1.5">
              <span
                className={cx(
                  "rounded-full shrink-0 z-10",
                  step.isSubtle ? "w-[6px] h-[6px] bg-fg-quaternary" : "w-[8px] h-[8px] bg-fg-tertiary",
                )}
              />
              {i < steps.length - 1 && <span className="w-[1.5px] flex-1 min-h-[8px] -mb-1.5 bg-border-primary" />}
            </div>
            {step.hasMessagePreview ? (
              <div className="flex-1 pb-2 flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setPreviewStep(step)}
                  className={cx(
                    "flex items-center gap-1.5 text-left font-medium",
                    step.isSubtle ? "text-xs leading-[18px] text-quaternary" : "text-sm leading-5 text-secondary",
                  )}
                >
                  <span className="underline decoration-dotted">{step.text}</span>
                  <Eye className="w-4 h-4 shrink-0" />
                  {step.step !== undefined && messageOverrides[step.step] && (
                    <span className="shrink-0 rounded-full px-1.5 py-px text-xs font-medium bg-utility-warning-50 text-utility-warning-700">
                      Edited
                    </span>
                  )}
                </button>
                {step.step !== undefined && stepsEditedBeforeDismissal.includes(step.step) && (
                  <EditedBeforeDismissalNotice
                    step={step.step}
                    onReset={(stepNumber) => onMessageOverrideChange(stepNumber, null)}
                  />
                )}
              </div>
            ) : (
              <p
                className={cx(
                  "flex-1 pb-2 font-medium",
                  step.isSubtle ? "text-xs leading-[18px] text-quaternary" : "text-sm leading-5 text-secondary",
                )}
              >
                {step.text}
              </p>
            )}
          </div>
        ))}
      </div>
      <ModalCustomMessageExample
        open={!!previewStep}
        onOpenChange={(open) => !open && setPreviewStep(undefined)}
        message={previewStep?.messageContent}
        editedMessage={previewOverride}
        previewContext={previewContext}
        onViewPolicy={onViewPolicy}
        onSave={
          previewStepNumber === undefined ? undefined : (content) => onMessageOverrideChange(previewStepNumber, content)
        }
      />
    </>
  );
};
